import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import EnrollmentService from "../../_services/EnrollmentService";
import { useEntityLabels } from "../../_context/EntityLabelsContext";
import {
  useRowSelection,
  type EnrollmentRow,
} from "./components/useEnrollmentSelection";
import SelectableEnrollmentTable, {
  headerCellStyle,
  bodyCellStyle,
} from "./components/SelectableEnrollmentTable";

// REGULAR enrollment status (StudentEnrollment.Status.REGULAR_ID). Only REGULAR
// enrollments are promotable — PASSOUT(5)/DROPOUT(3) are excluded (legacy Flow A).
const REGULAR_STATUS_ID = 1;

interface BulkPromoteModalProps {
  show: boolean;
  onHide: () => void;
  selectedEnquiryIds: number[];
  enquiryEnrollmentsMap: Record<number, any[]>;
  studentList: any[];
  onSuccess: () => void;
  /** "promote" (default) passes out the old enrollment; "addAnotherCourse" keeps it active. */
  promotionType?: "promote" | "addAnotherCourse";
}

interface ResultRow {
  enrollmentId: number;
  status: "promoted" | "skipped" | "not_found" | "error";
  newEnrollmentId?: number;
  reason?: string;
  error?: string;
}

interface Lookup {
  id: number;
  name: string;
}
interface BatchLookup extends Lookup {
  schoolId?: number | null;
  yearId?: number | null;
}

const statusBadgeStyle = (kind: ResultRow["status"]): React.CSSProperties => {
  const pal: Record<ResultRow["status"], { bg: string; fg: string; border: string }> = {
    promoted:  { bg: "#ecfdf5", fg: "#065f46", border: "var(--status-success-border)" },
    skipped:   { bg: "#eff6ff", fg: "#1e3a8a", border: "#bfdbfe" },
    not_found: { bg: "#fefce8", fg: "#854d0e", border: "var(--status-warning-border)" },
    error:     { bg: "#fef2f2", fg: "#991b1b", border: "var(--status-danger-border)" },
  };
  const p = pal[kind];
  return {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 10,
    background: p.bg,
    color: p.fg,
    border: `1px solid ${p.border}`,
    fontSize: "0.7rem",
    fontWeight: 600,
  };
};

const BulkPromoteModal: React.FC<BulkPromoteModalProps> = ({
  show,
  onHide,
  selectedEnquiryIds,
  enquiryEnrollmentsMap,
  studentList,
  onSuccess,
  promotionType = "promote",
}) => {
  const isAddCourse = promotionType === "addAnotherCourse";
  const verb = isAddCourse ? "Add Course" : "Promote";
  const { label } = useEntityLabels();

  const [schools, setSchools] = useState<Lookup[]>([]);
  const [years, setYears] = useState<Lookup[]>([]);
  const [currentYearId, setCurrentYearId] = useState<number | null>(null);
  const [batches, setBatches] = useState<BatchLookup[]>([]);
  const [batchCourses, setBatchCourses] = useState<
    Array<{ batchId: number; courseId: number | null; programId: number | null }>
  >([]);
  const [programs, setPrograms] = useState<Lookup[]>([]);
  const [courses, setCourses] = useState<Lookup[]>([]);
  const [statuses, setStatuses] = useState<Lookup[]>([]);
  const [isProgramBased, setIsProgramBased] = useState(false);
  const [loadingLookups, setLoadingLookups] = useState(false);

  // Cascade target selectors: Centre → Year → Course/Program → Section (batch).
  const [centreId, setCentreId] = useState<number | "">("");
  const [targetYearId, setTargetYearId] = useState<number | "">("");
  const [targetBatchId, setTargetBatchId] = useState<number | "">("");
  const [programId, setProgramId] = useState<number | "">("");
  const [courseId, setCourseId] = useState<number | "">("");
  // Enrollment Status applied to the NEW enrollment (enrollment_status_master).
  const [enrollmentStatusId, setEnrollmentStatusId] = useState<number | "">("");

  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<ResultRow[] | null>(null);
  const [summary, setSummary] = useState<any>(null);
  const [error, setError] = useState("");

  // Flatten enrollments under selected enquiries; join student names for display.
  const enrollments: EnrollmentRow[] = useMemo(() => {
    const nameByEnquiryId = new Map<number, string>();
    for (const s of studentList) nameByEnquiryId.set(s.enquiryId, s.name);

    // Only REGULAR enrollments are promotable; skip PASSOUT/DROPOUT. Dedupe by
    // enrollmentId (defensive — the backend already GROUP BYs per enrollment).
    const out: EnrollmentRow[] = [];
    const seen = new Set<number>();
    for (const eqId of selectedEnquiryIds) {
      const list = enquiryEnrollmentsMap[eqId] || [];
      for (const enr of list) {
        if (enr.statusId !== REGULAR_STATUS_ID) continue;
        if (seen.has(enr.enrollmentId)) continue;
        seen.add(enr.enrollmentId);
        out.push({
          enrollmentId: enr.enrollmentId,
          enquiryId: eqId,
          studentCode: enr.studentCode,
          statusId: enr.statusId,
          statusName: enr.statusName,
          batchName: enr.batchName,
          studentName: nameByEnquiryId.get(eqId) || "",
        });
      }
    }
    return out;
  }, [selectedEnquiryIds, enquiryEnrollmentsMap, studentList]);

  const { selectedIds, selectedEnrollmentIds, toggle, toggleAll, allSelected } =
    useRowSelection(enrollments, show);

  // Load lookups once when the modal opens; seed student-code overrides.
  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    setLoadingLookups(true);
    setError("");
    EnrollmentService.getLookups()
      .then((resp) => {
        if (cancelled) return;
        const d = resp.data?.data || {};
        setSchools(d.schools || []);
        setYears(d.years || []);
        setBatches(d.batches || []);
        setBatchCourses(d.batchCourses || []);
        setPrograms(d.programs || []);
        setCourses(d.courses || []);
        setStatuses(d.statuses || []);
        setIsProgramBased(!!d.isProgramBased);
        setCurrentYearId(d.currentYearId ?? null);
        // Default Year to the current academic year (legacy pre-populates it).
        if (d.currentYearId) setTargetYearId(d.currentYearId);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err?.response?.data?.message || "Failed to load options");
      })
      .finally(() => {
        if (!cancelled) setLoadingLookups(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show]);

  // ---- Cascade: Centre → Year → Course/Program → Section (target batch) ----
  // Batch ids available in the chosen centre (+ year, when picked).
  const centreBatchIds = useMemo(() => {
    const ids = new Set<number>();
    for (const b of batches) {
      if (centreId && b.schoolId !== centreId) continue;
      if (targetYearId && b.yearId != null && b.yearId !== targetYearId) continue;
      ids.add(b.id);
    }
    return ids;
  }, [batches, centreId, targetYearId]);

  // Courses (or programs, for program-based tenants) that have a batch in the
  // chosen centre (+ year) — via the batch_mapping links in `batchCourses`.
  const cascadeCourses = useMemo(() => {
    const wanted = new Set<number>();
    for (const bc of batchCourses) {
      if (!centreBatchIds.has(bc.batchId)) continue;
      const v = isProgramBased ? bc.programId : bc.courseId;
      if (v != null) wanted.add(v);
    }
    const source = isProgramBased ? programs : courses;
    return source.filter((c) => wanted.has(c.id));
  }, [batchCourses, centreBatchIds, isProgramBased, programs, courses]);

  // Section = the target batch: batches in the centre (+ year) mapped to the
  // chosen course/program.
  const sectionBatches = useMemo(() => {
    const selected = isProgramBased ? programId : courseId;
    if (!selected) return [] as BatchLookup[];
    const okBatchIds = new Set<number>();
    for (const bc of batchCourses) {
      if (!centreBatchIds.has(bc.batchId)) continue;
      const v = isProgramBased ? bc.programId : bc.courseId;
      if (v === Number(selected)) okBatchIds.add(bc.batchId);
    }
    return batches.filter((b) => okBatchIds.has(b.id));
  }, [batches, batchCourses, centreBatchIds, isProgramBased, programId, courseId]);

  // Cascade reset helpers — clear downstream selections when an upstream changes.
  const onCentreChange = (v: number | "") => {
    setCentreId(v);
    // Year is an independent required field (defaults to current year); keep it.
    setCourseId("");
    setProgramId("");
    setTargetBatchId("");
  };
  const onYearChange = (v: number | "") => {
    setTargetYearId(v);
    setCourseId("");
    setProgramId("");
    setTargetBatchId("");
  };
  const onCourseChange = (v: number | "") => {
    if (isProgramBased) setProgramId(v);
    else setCourseId(v);
    setTargetBatchId("");
  };

  const handleClose = () => {
    setCentreId("");
    setTargetYearId("");
    setTargetBatchId("");
    setProgramId("");
    setCourseId("");
    setEnrollmentStatusId("");
    setResults(null);
    setSummary(null);
    setError("");
    onHide();
  };

  const handleSubmit = async () => {
    setError("");
    if (!targetYearId) {
      setError("Select a Year.");
      return;
    }
    if (!targetBatchId) {
      setError("Select a Centre, Course/Grade and Section.");
      return;
    }
    if (statuses.length > 0 && !enrollmentStatusId) {
      setError("Select an Enrollment Status.");
      return;
    }
    if (selectedEnrollmentIds.length === 0) {
      setError("Select at least one enrollment.");
      return;
    }
    setSubmitting(true);
    try {
      const resp = await EnrollmentService.bulkPromote({
        targetBatchId: Number(targetBatchId),
        schoolId: centreId ? Number(centreId) : null,
        targetYearId: targetYearId ? Number(targetYearId) : null,
        programId: isProgramBased && programId ? Number(programId) : null,
        courseIds: !isProgramBased && courseId ? [Number(courseId)] : [],
        newEnrollmentStatusId: enrollmentStatusId ? Number(enrollmentStatusId) : null,
        installmentTemplateId: null,
        carryForwardTags: true,
        promotionType,
        remarks: null,
        students: enrollments
          .filter((e) => selectedIds.has(e.enrollmentId))
          .map((e) => ({ enrollmentId: e.enrollmentId, studentCode: null })),
      });
      const data = resp.data?.data || {};
      setResults(data.results || []);
      setSummary(data.summary || null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Bulk promote failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDoneAfterResults = () => {
    handleClose();
    onSuccess();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>
          {results
            ? isAddCourse
              ? "Add Another Course — Results"
              : "Promote Students — Results"
            : isAddCourse
            ? "Add Another Course"
            : "Promote Students"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <div
            style={{
              padding: "8px 12px",
              background: "var(--status-danger-bg)",
              color: "var(--status-danger)",
              borderRadius: 6,
              marginBottom: 12,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        {!results && (
          <>
            {loadingLookups ? (
              <div style={{ textAlign: "center", padding: 20, color: "var(--text-muted)" }}>
                <i className="fa fa-spinner fa-spin" /> Loading options…
              </div>
            ) : (
              <>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  <Form.Group className="mb-3" style={{ flex: "1 1 220px" }}>
                    <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>
                      Centre <span style={{ color: "var(--status-danger)" }}>*</span>
                    </Form.Label>
                    <Form.Select
                      size="sm"
                      value={centreId}
                      onChange={(e) =>
                        onCentreChange(e.target.value ? Number(e.target.value) : "")
                      }
                    >
                      <option value="">Select centre…</option>
                      {schools.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3" style={{ flex: "1 1 180px" }}>
                    <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>
                      {label("year", "Year")} <span style={{ color: "var(--status-danger)" }}>*</span>
                    </Form.Label>
                    <Form.Select
                      size="sm"
                      value={targetYearId}
                      onChange={(e) =>
                        onYearChange(e.target.value ? Number(e.target.value) : "")
                      }
                    >
                      <option value="">Select year…</option>
                      {years.map((y) => (
                        <option key={y.id} value={y.id}>
                          {y.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>

                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  <Form.Group className="mb-3" style={{ flex: "1 1 220px" }}>
                    <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>
                      {isProgramBased
                        ? label("program", "Program")
                        : label("enrollmentCourse", "Course")}{" "}
                      <span style={{ color: "var(--status-danger)" }}>*</span>
                    </Form.Label>
                    <Form.Select
                      size="sm"
                      value={isProgramBased ? programId : courseId}
                      onChange={(e) =>
                        onCourseChange(e.target.value ? Number(e.target.value) : "")
                      }
                      disabled={!centreId}
                    >
                      <option value="">
                        {centreId ? "Select…" : "Pick a centre first"}
                      </option>
                      {cascadeCourses.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3" style={{ flex: "1 1 220px" }}>
                    <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>
                      Section <span style={{ color: "var(--status-danger)" }}>*</span>
                    </Form.Label>
                    <Form.Select
                      size="sm"
                      value={targetBatchId}
                      onChange={(e) =>
                        setTargetBatchId(e.target.value ? Number(e.target.value) : "")
                      }
                      disabled={!(isProgramBased ? programId : courseId)}
                    >
                      <option value="">
                        {(isProgramBased ? programId : courseId)
                          ? "Select section…"
                          : "Pick a course first"}
                      </option>
                      {sectionBatches.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>

                {statuses.length > 0 && (
                  <Form.Group className="mb-3" style={{ maxWidth: 360 }}>
                    <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>
                      Enrollment Status <span style={{ color: "var(--status-danger)" }}>*</span>
                    </Form.Label>
                    <Form.Select
                      size="sm"
                      value={enrollmentStatusId}
                      onChange={(e) =>
                        setEnrollmentStatusId(e.target.value ? Number(e.target.value) : "")
                      }
                    >
                      <option value="">--Select--</option>
                      {statuses.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                )}

                <div
                  style={{
                    fontSize: 12,
                    color: "#854d0e",
                    background: "#fefce8",
                    border: "1px solid var(--status-warning-border)",
                    borderRadius: 6,
                    padding: "8px 10px",
                    marginBottom: 10,
                  }}
                >
                  Each student gets a <strong>new enrollment</strong> in the target batch.{" "}
                  {isAddCourse ? (
                    <>Their current enrollment <strong>stays active</strong> (an additional course).</>
                  ) : (
                    <>Their current enrollment is marked <strong>passout</strong>.</>
                  )}
                </div>

                <SelectableEnrollmentTable
                  enrollments={enrollments}
                  selectedIds={selectedIds}
                  allSelected={allSelected}
                  onToggle={toggle}
                  onToggleAll={toggleAll}
                  disabled={submitting}
                  caption={`Selected Students (${selectedEnrollmentIds.length} of ${enrollments.length} selected)`}
                />
              </>
            )}
          </>
        )}

        {results && (
          <>
            <div
              style={{
                display: "flex",
                gap: 12,
                marginBottom: 12,
                fontSize: 13,
                color: "var(--text-strong)",
              }}
            >
              <span>
                <strong>{summary?.total ?? results.length}</strong> processed
              </span>
              <span style={{ color: "var(--status-success-text)" }}>
                <strong>{summary?.promoted ?? 0}</strong> promoted
              </span>
              <span style={{ color: "var(--status-info-text)" }}>
                <strong>{summary?.skipped ?? 0}</strong> skipped
              </span>
              <span style={{ color: "var(--status-danger)" }}>
                <strong>{summary?.failed ?? 0}</strong> failed
              </span>
            </div>

            <div
              style={{
                maxHeight: 360,
                overflow: "auto",
                border: "1px solid var(--border-default)",
                borderRadius: 6,
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={headerCellStyle}>Enrollment</th>
                    <th style={headerCellStyle}>Outcome</th>
                    <th style={headerCellStyle}>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r) => (
                    <tr key={r.enrollmentId}>
                      <td style={bodyCellStyle}>#{r.enrollmentId}</td>
                      <td style={bodyCellStyle}>
                        <span style={statusBadgeStyle(r.status)}>{r.status}</span>
                      </td>
                      <td style={bodyCellStyle}>
                        {r.status === "promoted" && (
                          <span style={{ color: "var(--text-body)" }}>
                            New enrollment #{r.newEnrollmentId}
                          </span>
                        )}
                        {r.status === "skipped" && (
                          <span style={{ color: "var(--text-muted)" }}>
                            {r.reason === "already_promoted"
                              ? "Already promoted (has a next enrollment)"
                              : r.reason || ""}
                          </span>
                        )}
                        {r.status === "not_found" && (
                          <span style={{ color: "#854d0e" }}>Enrollment not found</span>
                        )}
                        {r.status === "error" && (
                          <span style={{ color: "var(--status-danger)" }}>{r.error}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        {!results ? (
          <>
            <Button variant="secondary" onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={
                submitting ||
                loadingLookups ||
                !targetYearId ||
                !targetBatchId ||
                (statuses.length > 0 && !enrollmentStatusId) ||
                selectedEnrollmentIds.length === 0
              }
            >
              {submitting ? (
                <>
                  <i className="fa fa-spinner fa-spin" /> {isAddCourse ? "Adding…" : "Promoting…"}
                </>
              ) : (
                <>
                  {verb} ({selectedEnrollmentIds.length})
                </>
              )}
            </Button>
          </>
        ) : (
          <Button variant="primary" onClick={handleDoneAfterResults}>
            Done
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default BulkPromoteModal;
